import type { Locale } from '../../../../../types';
import type { ScopedUtils } from '../../../shared/types';
import type {
    NumberFormat,
    NumberSettings,
    NumberFormatter,
    NumberFormatterInstancesMap,
} from '../types';
import { numberFormatterFactories } from '../numberFormatterFactories';
import { buildOverriddenFormatterKey } from './buildOverriddenFormatterKey';

/*
Кеш форматтеров для вызовов с переопределёнными настройками.

Без кеша каждый такой вызов конструирует новый Intl.NumberFormat — самая дорогая
операция Intl (резолв локали, инициализация данных). $n вызывается прямо из шаблонов,
то есть на каждом ре-рендере и на каждый элемент списка, поэтому пересоздание
масштабируется по длине списка.

Вытеснение не нужно: число комбинаций настроек ограничено сверху
(валюты × число знаков после запятой) и исчисляется десятками.
*/
export const createOverriddenFormattersCache = (
    locale: Locale,
    formatters: NumberFormatterInstancesMap,
    scopedUtils: ScopedUtils,
) => {
    const overriddenFormatters = new Map<string, NumberFormatter>();

    const getOverriddenFormatter = (
        format: NumberFormat,
        overriddenSettings: NumberSettings,
    ): NumberFormatter => {
        const key = buildOverriddenFormatterKey(format, overriddenSettings);
        const cachedFormatter = overriddenFormatters.get(key);

        if (cachedFormatter) {
            return cachedFormatter;
        }

        const formatter = numberFormatterFactories[format](
            locale,
            overriddenSettings,
            formatters,
            scopedUtils,
        );

        overriddenFormatters.set(key, formatter);

        return formatter;
    };

    return {
        getOverriddenFormatter,
    };
};
